import React, { useState, useEffect, useCallback } from 'react';
import { fetchChatbotActivity } from '../../../../services/chatbotActivity';
import ActivityGraph from './ActivityGraph';
import logger from '../../../../utils/logger';
import { Edit, Trash2, ExternalLink, RefreshCw, Bot, Power } from 'lucide-react';
import { auth } from '../../../../firebase/config/config';
import { toast } from 'sonner';

function ChatbotCard({ chatbot, onDelete, onEditChatbot }) {
  const { chatbot_id } = chatbot;
  const chatbotUrl = `/embed/chatbot?chatbotId=${chatbot_id}`;
  const [activityData, setActivityData] = useState(Array(30).fill(0));
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Widget enabled state
  const [widgetEnabled, setWidgetEnabled] = useState(chatbot.config?.widgetEnabled !== false);
  const [isToggling, setIsToggling] = useState(false);

  const loadActivityData = useCallback(async (forceRefresh = false) => {
    if (isRefreshing) return;

    if (forceRefresh) {
      setIsRefreshing(true);
    } else if (hasLoaded && !forceRefresh) {
      return;
    }

    try {
      const data = await fetchChatbotActivity(chatbot_id, forceRefresh);
      setActivityData(data);
      setHasLoaded(true);
    } catch (error) {
      logger.error('Failed to load activity data:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [chatbot_id, hasLoaded, isRefreshing]);

  useEffect(() => {
    loadActivityData(false);
  }, [loadActivityData]);

  const handleRefresh = (e) => {
    e.stopPropagation();
    loadActivityData(true);
  };

  const handleToggleWidget = async (e) => {
    e.stopPropagation();
    if (isToggling) return;

    setIsToggling(true);
    const newValue = !widgetEnabled;
    setWidgetEnabled(newValue); // Optimistic update

    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;

      // Update only the widgetEnabled field in config
      // We need to fetch current config first or just patch it if API supports it
      // Since our API expects full config, we use the chatbot prop's config + override
      // Ideally we should re-fetch, but for this UI action using prop + local change is standard

      const updatedConfig = {
        ...chatbot.config,
        widgetEnabled: newValue
      };

      const response = await fetch('/api/chatbot', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          chatbotId: chatbot_id,
          config: updatedConfig,
          userId: user?.uid
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update widget status');
      }
    } catch (error) {
      logger.error('Error toggling widget status:', error);
      toast.error(error.message || 'Failed to update widget status');
      setWidgetEnabled(!newValue); // Revert on error
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <div className={`group relative bg-white border rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md transition-all duration-200 p-5 flex flex-col ${!widgetEnabled ? 'border-red-100 bg-red-50/10' : 'border-slate-200'
      }`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3 overflow-hidden">
          {/* Bot Icon */}
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border shadow-sm overflow-hidden transition-colors ${widgetEnabled
              ? 'bg-violet-50 text-violet-600 border-violet-100/50'
              : 'bg-slate-100 text-slate-400 border-slate-200 grayscale'
            }`}>
            {chatbot.config?.botIconUrl ? (
              <img src={chatbot.config.botIconUrl} alt="Bot" className="w-full h-full object-cover" />
            ) : (
              <Bot size={20} />
            )}
          </div>

          <div className="min-w-0">
            <h3 className="font-bold text-slate-800 text-base truncate pr-2">
              {chatbot.config?.assistantName || 'Untitled Chatbot'}
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">ID:</span>
              <span className="text-[10px] font-mono text-slate-500 truncate max-w-[120px]" title={chatbot.chatbot_id}>
                {chatbot.chatbot_id}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* Status Toggle */}
          <button
            onClick={handleToggleWidget}
            title={widgetEnabled ? "Widget is Active (Click to Disable)" : "Widget is Disabled (Click to Enable)"}
            className={`flex items-center justify-center w-7 h-7 rounded-lg transition-all ${widgetEnabled
                ? 'text-green-500 hover:bg-green-50'
                : 'text-red-400 hover:bg-red-50'
              }`}
          >
            <Power size={14} className={isToggling ? 'animate-pulse' : ''} />
          </button>

          <button
            onClick={handleRefresh}
            className={`text-slate-300 hover:text-violet-500 transition-colors opacity-0 group-hover:opacity-100 ${isRefreshing ? 'animate-spin' : ''} `}
            title="Refresh Data"
          >
            <RefreshCw size={13} />
          </button>
        </div>
      </div>

      {/* Graph */}
      <div className={`w-full h-12 mb-3 mt-auto transition-opacity ${widgetEnabled ? 'opacity-80 group-hover:opacity-100' : 'opacity-30 grayscale'
        }`}>
        <ActivityGraph data={activityData} />
      </div>

      {/* Footer Buttons: Open | Edit | Delete */}
      <div className="grid grid-cols-3 gap-2">
        <a
          href={chatbotUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 py-2 rounded-md bg-slate-50 hover:bg-slate-100 text-slate-600 text-xs font-semibold transition-colors"
        >
          <ExternalLink size={12} />
          Open
        </a>
        <button
          onClick={() => onEditChatbot(chatbot_id)}
          className="flex items-center justify-center gap-2 py-2 rounded-md bg-slate-50 hover:bg-slate-100 text-slate-600 text-xs font-semibold transition-colors"
        >
          <Edit size={12} />
          Edit
        </button>
        <button
          onClick={() => onDelete(chatbot_id)}
          className="flex items-center justify-center gap-2 py-2 rounded-md bg-slate-50 hover:bg-red-50 text-slate-600 hover:text-red-600 text-xs font-semibold transition-colors"
        >
          <Trash2 size={12} />
          Delete
        </button>
      </div>
    </div>
  );
}

export default ChatbotCard;
